/**
 * useColumnPrefs — manages column visibility + widths per page.
 * Persists to localStorage so user preferences survive a refresh.
 *
 * Usage:
 *   const colPrefs = useColumnPrefs('dashboard', true);
 *   // colPrefs.columns   — visible column defs in order
 *   // colPrefs.widths    — { [colId]: px }
 *   // colPrefs.setWidth  — (id, px) => void
 *   // colPrefs.toggleCol — (id) => void  (required cols ignored)
 *   // colPrefs.allToggleable — every col that can be shown/hidden
 */

import { useState, useCallback } from 'react';

export const COLUMN_DEFS = [
  { id: 'owner',    label: 'Owner',    defaultWidth: 160, dashboardOnly: true, required: false },
  { id: 'event',    label: 'Event',    defaultWidth: 130, dashboardOnly: false, required: false },
  { id: 'task',     label: 'Task',     defaultWidth: 300, dashboardOnly: false, required: true  },
  { id: 'status',   label: 'Status',   defaultWidth: 130, dashboardOnly: false, required: false },
  { id: 'priority', label: 'Priority', defaultWidth: 100, dashboardOnly: false, required: false },
  { id: 'closure',  label: 'Closure',  defaultWidth: 90,  dashboardOnly: false, required: false },
  { id: 'time',     label: 'Time',     defaultWidth: 110, dashboardOnly: false, required: false },
  { id: 'remarks',  label: 'Remarks',  defaultWidth: 170, dashboardOnly: false, required: false },
  { id: 'comments', label: 'Comments', defaultWidth: 190, dashboardOnly: false, required: false },
];

function load(key) {
  try {
    const raw = localStorage.getItem(`orin-cols-${key}`);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return null;
}

function save(key, data) {
  try {
    localStorage.setItem(`orin-cols-${key}`, JSON.stringify(data));
  } catch { /* ignore */ }
}

export function useColumnPrefs(pageKey, showOwnerColumn) {
  /* Columns available for this page (strip dashboardOnly if no owner col) */
  const available = COLUMN_DEFS.filter(c => !c.dashboardOnly || showOwnerColumn);

  const saved = load(pageKey);

  const [visible, setVisible] = useState(() => {
    if (saved?.visible) {
      /* Always ensure required columns are visible — copy to avoid mutating cache */
      const v = [...saved.visible];
      COLUMN_DEFS.filter(c => c.required).forEach(c => { if (!v.includes(c.id)) v.push(c.id); });
      return v;
    }
    return available.map(c => c.id);
  });

  const [widths, setWidths] = useState(() => {
    if (saved?.widths) return { ...Object.fromEntries(COLUMN_DEFS.map(c => [c.id, c.defaultWidth])), ...saved.widths };
    return Object.fromEntries(COLUMN_DEFS.map(c => [c.id, c.defaultWidth]));
  });

  const setWidth = useCallback((id, w) => {
    setWidths(prev => {
      const next = { ...prev, [id]: Math.max(60, Math.round(w)) };
      save(pageKey, { visible, widths: next });
      return next;
    });
  }, [pageKey, visible]);

  const toggleCol = useCallback((id) => {
    const def = COLUMN_DEFS.find(c => c.id === id);
    if (def?.required) return; /* required columns can't be hidden */
    setVisible(prev => {
      const next = prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id];
      save(pageKey, { visible: next, widths });
      return next;
    });
  }, [pageKey, widths]);

  /* Ordered visible columns for this page */
  const columns = available.filter(c => visible.includes(c.id));

  /* Columns the user can toggle (available + not required) */
  const allToggleable = available.filter(c => !c.required);

  return { columns, visible, widths, setWidth, toggleCol, allToggleable };
}
